import { useCallback, useEffect, useState } from "react";

export interface MediaDeviceOption {
  deviceId: string;
  label: string;
}

function toOptions(
  devices: MediaDeviceInfo[],
  kind: MediaDeviceKind,
): MediaDeviceOption[] {
  return devices
    .filter((d) => d.kind === kind)
    .map((d) => ({
      deviceId: d.deviceId,
      label: d.label || d.deviceId || kind,
    }));
}

export function useMediaDeviceList(): {
  audioInputs: MediaDeviceOption[];
  audioOutputs: MediaDeviceOption[];
  refresh: () => Promise<void>;
} {
  const [audioInputs, setAudioInputs] = useState<MediaDeviceOption[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceOption[]>([]);

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setAudioInputs([]);
      setAudioOutputs([]);
      return;
    }
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setAudioInputs(toOptions(list, "audioinput"));
      setAudioOutputs(toOptions(list, "audiooutput"));
    } catch {
      setAudioInputs([]);
      setAudioOutputs([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) {
      return;
    }
    const onChange = () => {
      void refresh();
    };
    md.addEventListener("devicechange", onChange);
    return () => md.removeEventListener("devicechange", onChange);
  }, [refresh]);

  return { audioInputs, audioOutputs, refresh };
}
